import { useState } from "react";
import { AlertTriangle, Check, Loader2, Lock, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "../ui/sheet";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const fmtSEK = (v, dec = 0) =>
  v == null ? "—" : new Intl.NumberFormat("sv-SE", { minimumFractionDigits: dec, maximumFractionDigits: dec }).format(v);
const MISSING_LABELS = {
  EMPLOYMENT_TERMS: "Missing Employment Terms",
  FTE_SALARY: "Missing FTE Monthly Salary",
  PENSION: "Missing Pension Amount",
};
const missingLabel = (m, paramLabels) =>
  m.startsWith("PARAMETER:") ? `Missing parameter: ${paramLabels?.[m.slice(10)] || m.slice(10)}` : MISSING_LABELS[m] || m;

export default function PersonnelCostDetailPanel({
  open,
  onClose,
  employee, // { person, months }
  month,
  year,
  paramLabels,
  isAdmin,
  onChanged,
}) {
  const [overrideForm, setOverrideForm] = useState({ amount: "", comment: "" });
  const [saving, setSaving] = useState(false);

  if (!employee || !month) return null;
  const cell = employee.months[month] ?? employee.months[String(month)] ?? {};
  const person = employee.person;

  const saveOverride = async () => {
    setSaving(true);
    try {
      await api.put("/personnel-cost-overrides", {
        person_id: person.id,
        year,
        month,
        override_amount: Number(overrideForm.amount),
        comment: overrideForm.comment,
      });
      toast.success("Override saved");
      setOverrideForm({ amount: "", comment: "" });
      await onChanged();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const removeOverride = async () => {
    setSaving(true);
    try {
      await api.delete(`/personnel-cost-overrides/${cell.override.id}`);
      toast.success("Override removed — back to calculated");
      await onChanged();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const rows = cell.incomplete
    ? []
    : [
        { label: "FTE Monthly Salary", value: cell.fte_monthly_salary, fmt: (v) => `${fmtSEK(v)} kr`, note: "PAY-001 basis (100% FTE)" },
        { label: "Employment Percentage", value: cell.employment_percentage, fmt: (v) => `${v} %` },
        { label: "Adjusted Salary", value: cell.adjusted_salary, fmt: (v) => `${fmtSEK(v)} kr`, rule: "PAY-001" },
        { label: "Vacation Pay Rate", value: cell.vacation_pay_rate, fmt: (v) => `${v} %`, note: "effective-dated parameter" },
        { label: "Vacation Pay", value: cell.vacation_pay, fmt: (v) => `${fmtSEK(v, 2)} kr`, rule: "PAY-002" },
        { label: "Employer Contribution Rate", value: cell.employer_contribution_rate, fmt: (v) => `${v} %`, note: "effective-dated parameter" },
        { label: "Employer Contribution", value: cell.employer_contribution, fmt: (v) => `${fmtSEK(v, 2)} kr`, rule: "PAY-003", note: "on salary + vacation pay" },
        { label: "Pension", value: cell.pension_amount, fmt: (v) => `${fmtSEK(v)} kr`, rule: "PAY-004", note: "manual input, not auto-scaled" },
        { label: "Salary Tax Rate", value: cell.salary_tax_rate, fmt: (v) => `${v} %`, note: "effective-dated parameter" },
        { label: "Salary Tax", value: cell.salary_tax, fmt: (v) => `${fmtSEK(v, 2)} kr`, rule: "PAY-005" },
        { label: "Total Calculated Employment Cost", value: cell.total_calculated, fmt: (v) => `${fmtSEK(v, 2)} kr`, rule: "PAY-006", strong: true },
        { label: "Productive Capacity Hours", value: cell.productive_capacity_hours, fmt: (v) => `${fmtSEK(v, 1)} h`, rule: "PAY-007", note: "Standard Productive Hours × employment %" },
        { label: "Production Cost / Hour", value: cell.production_cost_per_hour, fmt: (v) => `${fmtSEK(v, 2)} kr/h`, rule: "PAY-008" },
      ];

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent className="w-[560px] sm:max-w-[560px] overflow-y-auto" data-testid="personnel-cost-panel">
        <SheetHeader>
          <SheetTitle className="font-heading">{person.name} — {MONTHS[month - 1]} {year}</SheetTitle>
          <SheetDescription>
            {cell.closed
              ? "Closed period — payroll actuals are not available in Build 4. Showing calculated forecast."
              : "Current calculated personnel cost"}
          </SheetDescription>
        </SheetHeader>

        {cell.closed && (
          <div className="mt-4 flex items-start gap-2 bg-secondary/50 border border-slate-200 rounded-lg p-3 text-xs text-slate-600" data-testid="panel-actual-unavailable">
            <Lock className="h-3.5 w-3.5 mt-0.5 text-slate-400" />
            Actual personnel cost is not available in Build 4. The value below is the calculated forecast, not a payroll actual.
          </div>
        )}

        {cell.incomplete && (
          <div className="mt-4 bg-[#FFFBEB] border border-[#FDE68A] rounded-lg p-3" data-testid="panel-incomplete">
            <div className="text-sm font-semibold text-[#B45309] flex items-center gap-1.5">
              <AlertTriangle className="h-4 w-4" /> Personnel cost incomplete
            </div>
            <ul className="mt-2 space-y-1">
              {(cell.missing || []).map((m) => (
                <li key={m} className="text-xs text-[#B45309]">• {missingLabel(m, paramLabels)}</li>
              ))}
            </ul>
          </div>
        )}

        {cell.terms_used && (
          <p className="mt-4 text-xs text-slate-500" data-testid="panel-terms-used">
            Terms used: {cell.terms_used.effective_from} → {cell.terms_used.effective_to || "ongoing"}
          </p>
        )}

        {!cell.incomplete && (
          <table className="w-full mt-4 border border-slate-200 rounded-lg overflow-hidden" data-testid="panel-breakdown">
            <tbody>
              {rows.map((r) => (
                <tr key={r.label} className={`border-b border-slate-100 ${r.strong ? "bg-slate-50" : ""}`}>
                  <td className="py-2 px-3 text-xs text-slate-600">
                    {r.label}
                    {r.rule && <span className="ml-1.5 text-[10px] font-mono text-slate-400">{r.rule}</span>}
                    {r.note && <span className="block text-[10px] text-slate-400">{r.note}</span>}
                  </td>
                  <td className={`py-2 px-3 text-right text-sm ${r.strong ? "font-bold text-slate-900" : "text-slate-800"}`}>
                    {r.fmt(r.value)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        <div className="mt-5 border-t border-slate-200 pt-4" data-testid="override-section">
          <h3 className="text-sm font-semibold text-slate-900 mb-2">Monthly override</h3>
          {cell.override ? (
            <div className="bg-[#FFFBEB] border border-[#FDE68A] rounded-lg p-3" data-testid="override-active">
              <div className="text-sm font-semibold text-slate-900">
                Override active: {fmtSEK(cell.override.override_amount)} kr
              </div>
              <div className="text-xs text-slate-600 mt-1">“{cell.override.comment}”</div>
              {!cell.incomplete && (
                <div className="text-xs text-slate-500 mt-1">
                  Calculated value preserved: {fmtSEK(cell.total_calculated, 2)} kr
                </div>
              )}
              {isAdmin && (
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-3"
                  onClick={removeOverride}
                  disabled={saving}
                  data-testid="override-remove-button"
                >
                  <Trash2 className="h-3.5 w-3.5 mr-1" /> Remove override
                </Button>
              )}
            </div>
          ) : isAdmin ? (
            <div className="space-y-3" data-testid="override-form">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Override amount (SEK)</Label>
                  <Input type="number" value={overrideForm.amount} onChange={(e) => setOverrideForm({ ...overrideForm, amount: e.target.value })} data-testid="override-amount-input" />
                </div>
                <div className="space-y-1.5">
                  <Label>Comment (required)</Label>
                  <Input value={overrideForm.comment} onChange={(e) => setOverrideForm({ ...overrideForm, comment: e.target.value })} placeholder="Why is this month different?" data-testid="override-comment-input" />
                </div>
              </div>
              <Button onClick={saveOverride} disabled={saving || !overrideForm.amount || !overrideForm.comment.trim()} className="bg-[#0052CC] hover:bg-[#0047B3]" data-testid="override-save-button">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4 mr-1" />} Save override
              </Button>
            </div>
          ) : (
            <p className="text-xs text-slate-500">No override — calculated value applies.</p>
          )}
        </div>

        <div className="mt-6 flex justify-end">
          <Button variant="outline" onClick={onClose} data-testid="personnel-cost-panel-close">Done</Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
